<template>
    <div class="search-results">
        <h3 class="search-results__header">
            <span class="search-results__title">
                {{ searchOptions.prompt }}
            </span>
            <span class="search-results__model white-space-nowrap">DALL·E 3</span>
            <span class="link-like search-results__load-more" @click="loadMore">Re-roll</span>
            <span v-if="completeCounter < numberOfResults" class="search-results__duration white-space-nowrap">
                Processing: {{ searchDuration }}s
            </span>
            <span v-else class="search-results__duration">Took: {{ searchDuration }}s</span>
        </h3>

        <div class="search-results-previews">
            <SearchPreview
                v-for="resultNumber in numberOfResults"
                :key="searchOptions.model + searchOptions.prompt + resultNumber"
                class="search-results-preview"
                :search-options="searchOptions"
                @complete="onComplete"
                @useImage="onUseImage"
            />
        </div>
    </div>
</template>

<script lang="ts">
import type { PropType } from "vue";

import SearchPreview from "@/components/AI/SearchPreview.vue";

import { type AiCustomInputs } from "@/enums/ai";

const DEFAULT_NUMBER_OF_RESULTS = 8;
const DEFAULT_NUMBER_OF_REROLLS = 4;

export default {
    name: "SearchResults",
    components: { SearchPreview },
    props: {
        searchOptions: {
            type: Object as PropType<AiCustomInputs>,
            required: true
        }
    },

    emits: ["useImage"],

    data() {
        return {
            completeCounter: 0,
            reRollCounter: 0,
            searchDuration: 0,
            searchStart: 0,
            updateInterval: undefined as undefined | ReturnType<typeof setInterval>
        };
    },

    computed: {
        numberOfResults() {
            return DEFAULT_NUMBER_OF_RESULTS + this.reRollCounter * this.numberOfReRolls;
        },

        numberOfReRolls() {
            return DEFAULT_NUMBER_OF_REROLLS;
        }
    },

    watch: {
        completeCounter() {
            if (this.completeCounter >= this.numberOfResults) {
                clearInterval(this.updateInterval);
            }
        }
    },

    mounted() {
        this.searchStart = Date.now();
        this.updateInterval = setInterval(this.updateDuration, 1000);
    },

    methods: {
        loadMore() {
            this.reRollCounter += 1;
        },

        onComplete() {
            this.completeCounter += 1;
        },

        onUseImage(url: string) {
            this.$emit("useImage", url);
        },

        updateDuration() {
            this.searchDuration = Math.floor((Date.now() - this.searchStart) / 1000);
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.search-results {
    margin-bottom: $spacing;
    display: flex;
    flex: 1 1 100%;
    width: 100%;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    &__header {
        display: flex;
        justify-content: flex-start;
        align-items: flex-end;
        width: 100%;
        margin-bottom: $spacing;
        line-height: 1;
    }

    &__model {
        font-weight: normal;
        margin-right: $spacing;
    }

    &__duration {
        margin-right: $spacing;
        font-weight: normal;
        font-size: 12px;
        white-space: nowrap;
    }

    &__title {
        overflow: hidden;
        text-overflow: ellipsis;
        margin-right: $spacing-larger;
    }

    &-previews {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-auto-rows: 1fr;
        width: 100%;
        flex-wrap: wrap;
    }

    &-preview {
        flex: 1 1 22%;
        margin: 5px;
        position: relative;
        background: #ffffff;
    }

    &__load-more {
        font-size: 14px;
        margin: 0 $spacing 0 auto;
    }

    &__view-change {
        font-weight: normal;
        padding: 0 $spacing;
        margin-right: auto;
        cursor: pointer;

        span {
            font-weight: bold;
        }
    }
}
</style>
